import LocalDiffuserDataService from "@/services/LocalDiffuserDataService"
import { defineStore } from "pinia"
import { useAuthenticationStore } from "./authentication"
import { useAlertStore } from "./alert"
import { i18n } from "@/i18n"
import { AlertType } from "@/static/Alert"
import type { ILocalListing } from "@/types/Listing.types"
import type { IApiSolicitationItem, ISolicitationItem } from "@/types/Solicitation.types"
import { normalizeApiSolicitationToItem } from "@/utils/notificationCenterMappers"
import { isDraftSolicitationState, SolicitationState } from "@/static/Solicitation"
import { useEstablishmentStore } from "./establishment"
import { useAccountStore } from "./account"

/** Hors du state Pinia pour ne pas être persisté (Promise non sérialisable). */
let solicitationsFetchSeq = 0
let solicitationsInFlightKey: string | null = null
let solicitationsInFlight: Promise<void> | null = null
let dialogueSolicitationsInFlightKey: string | null = null
let dialogueSolicitationsInFlight: Promise<void> | null = null

function solicitationsRequestKey(externalIds: number[]): string {
  return [...new Set(externalIds.map(Number).filter((id) => Number.isFinite(id)))]
    .sort((a, b) => a - b)
    .join(",")
}

export const useLocalDiffuserStore = defineStore("localDiffuser", {
  state: () => ({
    draftSolicitations: [] as ISolicitationItem[],

    currentSolicitations: [] as ISolicitationItem[],

    archivedSolicitations: [] as ISolicitationItem[],

    isLoading: false,

    localDiffuserLogoUrl: null as String | null,
  }),

  getters: {
    pendingSolicitationsCount: (state): number =>
      state.draftSolicitations.length + state.currentSolicitations.length,

    hasSavingDraftSolicitations: (state): boolean =>
      state.draftSolicitations.some(
        (solicitation) => solicitation.state === SolicitationState.SAVING_DRAFT
      ),
  },

  actions: {
    clear() {
      solicitationsFetchSeq += 1
      solicitationsInFlightKey = null
      solicitationsInFlight = null
      dialogueSolicitationsInFlightKey = null
      dialogueSolicitationsInFlight = null

      this.draftSolicitations = [] as ISolicitationItem[]
      this.currentSolicitations = [] as ISolicitationItem[]
      this.archivedSolicitations = [] as ISolicitationItem[]

      this.isLoading = false

      this.localDiffuserLogoUrl = null as String | null
    },

    solicitations(callback?: (archivedSolicitations: ISolicitationItem[]) => void) {
      const establishmentStore = useEstablishmentStore()

      const localDiffusers: ILocalListing[] = establishmentStore.currentEstablishment.local_diffusers ?? []
      const externalIds = localDiffusers.map((localDiffuser) => Number(localDiffuser.external_id))

      return this.solicitationsForExternalIds(externalIds, callback)
    },

    /**
     * Sollicitations de l'espace Mes échanges : un seul appel API filtré par compte OT Dahub.
     */
    solicitationsForTourismInformationCenter(
      tourismInformationCenterId: number | null,
      callback?: (archivedSolicitations: ISolicitationItem[]) => void,
      options?: { force?: boolean; silent?: boolean }
    ) {
      const requestKey = tourismInformationCenterId == null ? "" : String(tourismInformationCenterId)

      if (!options?.force && dialogueSolicitationsInFlight && dialogueSolicitationsInFlightKey === requestKey) {
        return dialogueSolicitationsInFlight.then(() => {
          if (callback) {
            callback(this.archivedSolicitations)
          }
        })
      }

      const requestSeq = ++solicitationsFetchSeq
      const silent = options?.silent === true

      if (!silent) {
        this.isLoading = true
      }

      const applyIfCurrent = (draftSolicitations: ISolicitationItem[], currentSolicitations: ISolicitationItem[], archivedSolicitations: ISolicitationItem[]) => {
        if (requestSeq !== solicitationsFetchSeq) {
          return
        }

        this.draftSolicitations = this._sortSolicitationBydate(this._uniqueSolicitations(draftSolicitations))
        this.currentSolicitations = this._sortSolicitationBydate(this._uniqueSolicitations(currentSolicitations))
        this.archivedSolicitations = this._sortSolicitationBydate(this._uniqueSolicitations(archivedSolicitations))

        if (callback) {
          callback(this.archivedSolicitations)
        }

        if (!silent) {
          this.isLoading = false
        }
      }

      const applyEmpty = () => applyIfCurrent([], [], [])

      const fetchPromise =
        tourismInformationCenterId == null
          ? Promise.resolve().then(applyEmpty)
          : LocalDiffuserDataService.getDialogueSolicitations(tourismInformationCenterId)
              .then((res) => {
                const draftSolicitations: ISolicitationItem[] = []
                const currentSolicitations: ISolicitationItem[] = []
                const archivedSolicitations: ISolicitationItem[] = []

                const resSolicitations = (res.data.data.solicitations ?? []) as IApiSolicitationItem[]
                resSolicitations.forEach((apiSolicitation) => {
                  const solicitation = normalizeApiSolicitationToItem(apiSolicitation)

                  if (isDraftSolicitationState(solicitation.state)) {
                    draftSolicitations.push(solicitation)
                  } else {
                    currentSolicitations.push(solicitation)
                  }
                })

                const resArchivedSolicitations = (res.data.data.old_solicitations ?? []) as IApiSolicitationItem[]
                archivedSolicitations.push(
                  ...resArchivedSolicitations.map((apiSolicitation) => normalizeApiSolicitationToItem(apiSolicitation))
                )

                applyIfCurrent(draftSolicitations, currentSolicitations, archivedSolicitations)
              })
              .catch((e) => {
                const alertStore = useAlertStore()
                switch (e.response?.status) {
                  case 401:
                    break
                  case 503:
                    alertStore.push(AlertType.error, i18n.global.t("common.errors.hubo_server_error"), e.message)
                    break
                  default:
                    alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
                    break
                }

                applyEmpty()
              })

      dialogueSolicitationsInFlightKey = requestKey
      dialogueSolicitationsInFlight = fetchPromise.finally(() => {
        if (requestSeq === solicitationsFetchSeq) {
          dialogueSolicitationsInFlightKey = null
          dialogueSolicitationsInFlight = null
        }
      })

      return dialogueSolicitationsInFlight
    },

    /**
     * Point d'entrée des sollicitations hors espace Dialogue (établissement, centre de notifications).
     * L'appel unitaire par diffuseur sera remplacé ici par l'appel batch Dahub, sans impact
     * sur les vues.
     *
     * Les appels concurrents avec les mêmes ids réutilisent la Promise en cours. Un nouvel
     * appel (changement d'OT, refresh après refus) invalide le précédent : les résultats
     * sont collectés localement puis assignés une seule fois, pour éviter les concaténations
     * en parallèle qui dupliquaient les listes.
     */
    solicitationsForExternalIds(
      externalIds: number[],
      callback?: (archivedSolicitations: ISolicitationItem[]) => void,
      options?: { force?: boolean; silent?: boolean }
    ) {
      const uniqueIds = [...new Set(externalIds.map(Number).filter((id) => Number.isFinite(id)))]
      const requestKey = solicitationsRequestKey(uniqueIds)

      if (!options?.force && solicitationsInFlight && solicitationsInFlightKey === requestKey) {
        return solicitationsInFlight.then(() => {
          if (callback) {
            callback(this.archivedSolicitations)
          }
        })
      }

      const requestSeq = ++solicitationsFetchSeq
      const silent = options?.silent === true

      if (!silent) {
        this.isLoading = true
      }

      const draftSolicitations: ISolicitationItem[] = []
      const currentSolicitations: ISolicitationItem[] = []
      const archivedSolicitations: ISolicitationItem[] = []

      const applyIfCurrent = () => {
        if (requestSeq !== solicitationsFetchSeq) {
          return
        }

        this.draftSolicitations = this._sortSolicitationBydate(this._uniqueSolicitations(draftSolicitations))
        this.currentSolicitations = this._sortSolicitationBydate(this._uniqueSolicitations(currentSolicitations))
        this.archivedSolicitations = this._sortSolicitationBydate(this._uniqueSolicitations(archivedSolicitations))

        if (callback) {
          callback(this.archivedSolicitations)
        }

        if (!silent) {
          this.isLoading = false
        }
      }

      const fetchPromise =
        uniqueIds.length === 0
          ? Promise.resolve().then(applyIfCurrent)
          : Promise.all(
              uniqueIds.map((externalId) =>
                LocalDiffuserDataService.getLocalDiffuserSolicitations(externalId).then(
                  (res) => {
                    const resSolicitations: ISolicitationItem[] = res.data.data.solicitations

                    if (resSolicitations && resSolicitations.length) {
                      resSolicitations.forEach((solicitation) => {
                        const tagged = this._tagProvider(solicitation, externalId)

                        if (isDraftSolicitationState(tagged.state)) {
                          draftSolicitations.push(tagged)
                        } else {
                          currentSolicitations.push(tagged)
                        }
                      })
                    }

                    const resArchivedSolicitations: ISolicitationItem[] = res.data.data.old_solicitations
                    if (resArchivedSolicitations && resArchivedSolicitations.length) {
                      archivedSolicitations.push(
                        ...resArchivedSolicitations.map((solicitation) => this._tagProvider(solicitation, externalId))
                      )
                    }
                  },
                  (e) => {
                    const alertStore = useAlertStore()
                    switch (e.response.status) {
                      case 401:
                        break
                      case 503:
                        alertStore.push(AlertType.error, i18n.global.t("common.errors.hubo_server_error"), e.message)
                        break
                      default:
                        alertStore.push(AlertType.error, i18n.global.t("common.errors.default"), e.message)
                        break
                    }
                  }
                )
              )
            ).then(applyIfCurrent)

      solicitationsInFlightKey = requestKey
      solicitationsInFlight = fetchPromise.finally(() => {
        if (requestSeq === solicitationsFetchSeq) {
          solicitationsInFlightKey = null
          solicitationsInFlight = null
        }
      })

      return solicitationsInFlight
    },

    logo() {
      const authStore = useAuthenticationStore()
      const accountStore = useAccountStore()
      const establishmentStore = useEstablishmentStore()

      const candidates = [
        establishmentStore.currentEstablishment,
        ...accountStore.establishments,
      ].filter(Boolean)

      const establishmentWithLocalDiffuser = candidates.find(
        (establishment) => (establishment.local_diffusers?.length ?? 0) > 0
      )

      if (!establishmentWithLocalDiffuser?.id) {
        return
      }

      LocalDiffuserDataService.getLocalDiffuserLogo(establishmentWithLocalDiffuser.id, authStore.currentHeaders).then((res) => {
        if (res.data.data.logo) {
          this.localDiffuserLogoUrl = res.data.data.logo
        }
      })
    },

    _tagProvider(solicitation: ISolicitationItem, externalId: number): ISolicitationItem {
      return {
        ...solicitation,
        provider_external_id: externalId,
      }
    },

    _sortSolicitationBydate(solicitations: ISolicitationItem[]) {
      return solicitations.sort((a: ISolicitationItem, b: ISolicitationItem) => {
        return +new Date(b.created_at) - +new Date(a.created_at)
      })
    },

    _uniqueSolicitations(solicitations: ISolicitationItem[]) {
      const seen = new Set<string>()

      return solicitations.filter((solicitation) => {
        const key = solicitation.token
          ? `t:${solicitation.token}`
          : solicitation.id != null
            ? `i:${solicitation.id}`
            : null

        if (!key) {
          return true
        }

        if (seen.has(key)) {
          return false
        }

        seen.add(key)
        return true
      })
    },

    refuseAdhesion(solicitationId: number) {
      return LocalDiffuserDataService.refuseAdhesion(solicitationId)
    },
  },

  persist: {
    enabled: true,
  },
})
